import React, { useCallback, useState } from 'react';
import { every } from 'lodash';
import Form, { Field } from 'mobx-react-form';
import { observer } from 'mobx-react-lite';
import { Button, Grid, Typography } from '@mui/material';

import { RentRollTableColumn, ResidentialRentRollUnit } from 'types/underwriting/income-approach';
import RentRollTable from '../RentRollTable';
import { FormOptions, wrapForm } from 'components/shared/form';
import NumericInput from 'components/shared/inputs/NumericInput';

import { formOptions } from 'forms/utils/formOptions';
import { plugins } from 'forms/utils/formPlugins';
import { RENT_ROLL_LABELS } from '../../constants';
import { useRootStore } from 'stores/RootStore';
import { rentRollFormFields, rentRollFormHelpers } from './rentRollFormConfig';
import { AddMultipleUnitsModal, UnitMix } from './AddMultipleUnitsModal';

const formConfig: FormOptions = {
  fields: rentRollFormFields,
  options: {
    ...formOptions,
    validateOnChange: true,
  },
  plugins,
};

type ResidentialRentRollProps = {
  form: Form;
};

const isRentRollUnitEmpty = (unit: Omit<ResidentialRentRollUnit, 'id'>) => {
  return every(unit, (value) => !value);
};

const ResidentialRentRoll = observer(({ form }: ResidentialRentRollProps) => {
  const { underwritingPageStore } = useRootStore();
  const { property } = underwritingPageStore.underwritingData || {};
  const columns: RentRollTableColumn[] = rentRollFormHelpers.getUnitsTableColumns();
  const rentRollField: Field = form.$('incomeApproach.residential.rentRoll');
  const rentRoll: ResidentialRentRollUnit[] = rentRollField.value;

  const [showAddMultipleUnitsModal, setShowAddMultipleUnitsModal] = useState(false);

  const addMultipleUnits = useCallback(
    (unitMixes: UnitMix[]) => {
      unitMixes.forEach((unitMix) => {
        for (let i = 0; i < unitMix.numberOfUnits; i++) {
          const index = rentRoll.findIndex((unit) => {
            const { id, ...fields } = unit;
            return isRentRollUnitEmpty(fields);
          });
          if (index !== -1) {
            rentRoll[index] = {
              id: rentRoll[index].id,
              bedrooms: unitMix.bedrooms,
              bathrooms: unitMix.bathrooms,
              squareFootage: unitMix.squareFeet,
              rent: unitMix.rent,
              outdoor: unitMix.outdoorSpace,
              rentType: unitMix.rentType,
              unitLayout: unitMix.unitType,
              leaseStatus: unitMix.leaseStatus,
            };
          }
        }
      });

      rentRollField.set(rentRoll);

      setShowAddMultipleUnitsModal(false);
    },
    [rentRollField, rentRoll]
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">Borrower Rent Roll</Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item container xs={4} direction="column">
            <NumericInput
              disabled
              size="small"
              value={property?.residentialUnits ?? 0}
              label={RENT_ROLL_LABELS.NUMBER_OF_RESIDENTIAL_UNITS}
              sx={{ width: '200px' }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowAddMultipleUnitsModal(true)}
              sx={{ mt: 2, width: '200px' }}
            >
              Add Multiple Units
            </Button>
          </Grid>
          <Grid item xs={12}>
            <RentRollTable form={form} columns={columns} />
          </Grid>
          <AddMultipleUnitsModal
            open={showAddMultipleUnitsModal}
            maxUnitNumber={rentRoll.filter(({ id, ...fields }) => isRentRollUnitEmpty(fields)).length}
            onClose={() => setShowAddMultipleUnitsModal(false)}
            onAdd={addMultipleUnits}
          />
        </Grid>
      </Grid>
    </Grid>
  );
});

export default wrapForm({
  ...formConfig,
})(ResidentialRentRoll);
